import {
  useEffect,
  useMemo,
  useRef,
} from 'react';

import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  AppDispatch,
  RootState,
} from '../../store/store';

import ReturnEventService, {
  ReturnEventSubmitOptions,
} from '../../services/fiscal/ReturnEventService';

import {
  addReturnDocument,
  clearReturnPreview,
  removeReturnDocument,
  resetReturnEvent,
  setReturnBusy,
  setReturnDocumentLines,
  setReturnDocumentSearch,
  setReturnError,
  setReturnLineDiscount,
  setReturnLineQuantity,
  setReturnLineSelected,
  setReturnLineUnitPrice,
  setReturnPreview,
  setReturnPreviewLoading,
  setReturnResult,
  setSearchingReturnDocuments,
} from '../../store/slices/fiscal/returnEventSlice';

import {
  ReturnDocumentCandidate,
  ReturnEligibility,
  ReturnRequest,
} from '../../types/fiscal/ReturnEvent';

import {
  validateReturnQuantity,
} from '../../features/fiscalReturn/returnValidation';

import useAlerts from '../useAlerts';

// =====================================================
// 📦 Opciones
// =====================================================

interface UseReturnEventOptions {
  isOpen?: boolean;

  initialSaleId?: number | null;

  onCreated?: () => void;
}

// =====================================================
// 🔑 Helpers
// =====================================================

export const returnLineKey = (
  saleId: number,
  productId: number
) =>
  `${saleId}:${productId}`;

// =====================================================
// 🪝 Hook
// =====================================================

export const useReturnEvent = (
  options: UseReturnEventOptions = {}
) => {
  const {
    isOpen = false,
    initialSaleId,
    onCreated,
  } = options;

  const dispatch: AppDispatch =
    useDispatch();

  const { show } =
    useAlerts();

  // =====================================================
  // Redux
  // =====================================================

  const {
    documentResults,
    documentPaginator,
    searchingDocuments,

    documents,

    selectedLines,
    quantities,
    unitPrices,
    discounts,

    preview,
    previewLoading,
    result,

    busy,
    error,
  } = useSelector(
    (state: RootState) =>
      state.returnEvent
  );

  // =====================================================
  // 🔒 Control no serializable
  // =====================================================

  const idempotencyKey =
    useRef<string>();

  const submitLock =
    useRef(false);

  // =====================================================
  // 🧮 Request derivado
  // =====================================================

  const request =
    useMemo<ReturnRequest>(() => {
      const lines =
        documents
          .flatMap(
            ({ eligibility }) =>
              eligibility.lines.map(
                (line) => {
                  const key =
                    returnLineKey(
                      eligibility.saleId,
                      line.productId
                    );

                  return {
                    saleId:
                      eligibility.saleId,

                    productId:
                      line.productId,

                    quantity:
                      Number(
                        quantities[key] || 0
                      ),

                    unitPrice:
                      unitPrices[key] !== undefined &&
                      unitPrices[key] !== ''
                        ? Number(unitPrices[key])
                        : undefined,

                    discount:
                      discounts[key] !== undefined &&
                      discounts[key] !== ''
                        ? Number(discounts[key])
                        : undefined,

                    selected:
                      Boolean(
                        selectedLines[key]
                      ),
                  };
                }
              )
          )
          .filter(
            (line) =>
              line.selected &&
              line.quantity > 0
          )
          .map(
            ({
              selected: _selected,
              ...line
            }) => line
          );

      return {
        saleIds: [
          ...new Set(
            lines.map(
              (line) => line.saleId
            )
          ),
        ],

        lines,
      };
    }, [
      documents,
      quantities,
      unitPrices,
      discounts,
      selectedLines,
    ]);

  // =====================================================
  // ⚠️ Errores de cantidades
  // =====================================================

  const validationErrors =
    useMemo(() => {
      return documents.flatMap(
        ({ eligibility }) =>
          eligibility.lines.flatMap(
            (line) => {
              const key =
                returnLineKey(
                  eligibility.saleId,
                  line.productId
                );

              if (
                !selectedLines[key]
              ) {
                return [];
              }

              const validationError =
                validateReturnQuantity(
                  quantities[key] || '',
                  String(
                    line.availableQuantity
                  )
                );

              return validationError
                ? [
                    `${line.description}: ${validationError}`,
                  ]
                : [];
            }
          )
      );
    }, [
      documents,
      quantities,
      selectedLines,
    ]);

  // =====================================================
  // 🧹 Invalidar preview
  // =====================================================

  const invalidatePreview = () => {
    dispatch(
      clearReturnPreview()
    );

    idempotencyKey.current =
      undefined;
  };

  // =====================================================
  // 🔍 BUSCAR DOCUMENTOS
  // =====================================================

  const handlerSearchDocuments =
    async (
      query = '',
      page = 0,
      size = 10
    ) => {
      try {
        dispatch(
          setSearchingReturnDocuments(
            true
          )
        );

        const response =
          await ReturnEventService.searchDocuments(
            query,
            page,
            size
          );

        if (
          response?.statusCode === 200 &&
          response.data
        ) {
          dispatch(
            setReturnDocumentSearch(
              response.data
            )
          );

          return response.data;
        }

        show(
          'error',
          'Error',
          response?.message ||
            'No se pudieron consultar los documentos disponibles.'
        );

        return null;
      } catch (error: any) {
        show(
          'error',
          error?.response?.data
            ?.title ||
            'Error al consultar documentos',
          error?.response?.data
            ?.message ||
            'No fue posible consultar los documentos.'
        );

        return null;
      } finally {
        dispatch(
          setSearchingReturnDocuments(
            false
          )
        );
      }
    };

  // =====================================================
  // 📄 AGREGAR DOCUMENTO
  // =====================================================

  const handlerAddDocument =
    async (
      candidate?: ReturnDocumentCandidate,
      internalSaleId?: number
    ) => {
      const saleId =
        candidate?.saleId ??
        internalSaleId;

      if (!saleId) {
        return false;
      }

      const exists =
        documents.some(
          (document) =>
            document.eligibility
              .saleId === saleId
        );

      if (exists) {
        return false;
      }

      dispatch(
        setReturnBusy(true)
      );

      dispatch(
        setReturnError(null)
      );

      try {
        const response =
          await ReturnEventService.eligibility(
            saleId
          );

        const eligibility =
          response.data;

        if (!eligibility) {
          throw new Error(
            'No se recibió información de elegibilidad.'
          );
        }

        if (
          !eligibility.eligible
        ) {
          show(
            'warning',
            'Documento no elegible',
            eligibility.blockingReasons
              ?.join(' ') ||
              'El documento no puede utilizarse en un Evento de Retorno.'
          );

          return false;
        }

        dispatch(
          addReturnDocument({
            candidate,
            eligibility,
          })
        );

        invalidatePreview();

        return true;
      } catch (error: any) {
        show(
          'error',
          error?.response?.data
            ?.title ||
            'No se pudo consultar',
          error?.response?.data
            ?.message ||
            'Revise el documento e intente nuevamente.'
        );

        return false;
      } finally {
        dispatch(
          setReturnBusy(false)
        );
      }
    };

  // =====================================================
  // 🗑️ QUITAR DOCUMENTO
  // =====================================================

  const handlerRemoveDocument = (
    saleId: number
  ) => {
    dispatch(
      removeReturnDocument(
        saleId
      )
    );

    invalidatePreview();
  };

  // =====================================================
  // ☑️ SELECCIONAR LÍNEA
  // =====================================================

  const handlerChangeSelection = (
    saleId: number,
    productId: number,
    selected: boolean,
    availableQuantity: number,
    unitPrice?: number,
    discount?: number
  ) => {
    const key =
      returnLineKey(
        saleId,
        productId
      );

    dispatch(
      setReturnLineSelected({
        key,
        selected,

        quantity: selected
          ? quantities[key] ||
            String(
              availableQuantity
            )
          : '',

        unitPrice: selected
          ? unitPrices[key] ||
            String(unitPrice ?? '')
          : '',

        discount: selected
          ? discounts[key] ||
            String(discount ?? '')
          : '',
      })
    );

    invalidatePreview();
  };

  // =====================================================
  // 🔢 CAMBIAR CANTIDAD
  // =====================================================

  const handlerChangeQuantity = (
    saleId: number,
    productId: number,
    quantity: string
  ) => {
    const key =
      returnLineKey(
        saleId,
        productId
      );

    dispatch(
      setReturnLineQuantity({
        key,
        quantity,
      })
    );

    invalidatePreview();
  };

  // =====================================================
  // 💲 CAMBIAR PRECIO UNITARIO
  // =====================================================

  const handlerChangeUnitPrice = (
    saleId: number,
    productId: number,
    unitPrice: string
  ) => {
    const key =
      returnLineKey(
        saleId,
        productId
      );

    dispatch(
      setReturnLineUnitPrice({
        key,
        unitPrice,
      })
    );

    invalidatePreview();
  };

  // =====================================================
  // 🏷️ CAMBIAR DESCUENTO
  // =====================================================

  const handlerChangeDiscount = (
    saleId: number,
    productId: number,
    discount: string
  ) => {
    const key =
      returnLineKey(
        saleId,
        productId
      );

    dispatch(
      setReturnLineDiscount({
        key,
        discount,
      })
    );

    invalidatePreview();
  };

  // =====================================================
  // ♻️ SELECCIONAR TODO
  // =====================================================

  const handlerSetAll = (
    eligibility: ReturnEligibility,
    selected: boolean
  ) => {
    dispatch(
      setReturnDocumentLines({
        saleId:
          eligibility.saleId,

        lines:
          eligibility.lines.map(
            (line) => ({
              productId:
                line.productId,

              availableQuantity:
                line.availableQuantity,

              unitPrice:
                line.unitPrice,

              discount:
                line.discount,
            })
          ),

        selected,
      })
    );

    invalidatePreview();
  };

  // =====================================================
  // 🧮 PREVIEW (silenciosa, disparada en vivo)
  // =====================================================

  const runPreview = async () => {
    dispatch(
      setReturnPreviewLoading(true)
    );

    try {
      const response =
        await ReturnEventService.preview(
          request
        );

      if (response.data) {
        dispatch(
          setReturnPreview(
            response.data
          )
        );
      }
    } catch {
      dispatch(
        setReturnPreview(null)
      );
    } finally {
      dispatch(
        setReturnPreviewLoading(
          false
        )
      );
    }
  };

  // =====================================================
  // 🧮 PREVIEW (manual, con validaciones y feedback)
  // =====================================================

  const handlerPreview =
    async () => {
      if (!request.lines.length) {
        show(
          'warning',
          'Productos requeridos',
          'Seleccione al menos un producto con cantidad mayor que cero.'
        );

        return false;
      }

      if (
        validationErrors.length
      ) {
        show(
          'warning',
          'Cantidades inválidas',
          validationErrors.join(
            ' '
          )
        );

        return false;
      }

      dispatch(
        setReturnPreviewLoading(true)
      );

      try {
        const response =
          await ReturnEventService.preview(
            request
          );

        if (!response.data) {
          throw new Error(
            'No se recibió la previsualización fiscal.'
          );
        }

        dispatch(
          setReturnPreview(
            response.data
          )
        );

        return true;
      } catch (error: any) {
        show(
          'error',
          error?.response?.data
            ?.title ||
            'Previsualización rechazada',
          error?.response?.data
            ?.message ||
            'La selección no es fiscalmente válida.'
        );

        return false;
      } finally {
        dispatch(
          setReturnPreviewLoading(
            false
          )
        );
      }
    };

  // =====================================================
  // ⏱️ PREVIEW EN VIVO (debounced)
  // =====================================================

  useEffect(() => {
    if (
      !request.lines.length ||
      validationErrors.length
    ) {
      dispatch(
        clearReturnPreview()
      );

      return;
    }

    const timer = window.setTimeout(() => {
      void runPreview();
    }, 600);

    return () =>
      window.clearTimeout(timer);
  }, [
    request,
    validationErrors.length,
  ]);

  // =====================================================
  // 🚀 GENERAR EVENTO
  // =====================================================

  const handlerSubmit =
    async (
      submitOptions: ReturnEventSubmitOptions = {}
    ) => {
      if (
        submitLock.current ||
        !preview?.valid
      ) {
        return false;
      }

      submitLock.current =
        true;

      idempotencyKey.current ||=
        crypto.randomUUID();

      dispatch(
        setReturnBusy(true)
      );

      try {
        const response =
          await ReturnEventService.submit(
            request,
            idempotencyKey.current,
            submitOptions
          );

        if (!response.data) {
          throw new Error(
            'No se recibió información del evento generado.'
          );
        }

        dispatch(
          setReturnResult(
            response.data
          )
        );

        onCreated?.();

        show(
          response.type,
          response.title,
          response.message
        );

        return true;
      } catch (error: any) {
        show(
          'error',
          error?.response?.data
            ?.title ||
            'No se pudo crear',
          error?.response?.data
            ?.message ||
            'El Evento de Retorno no fue generado.'
        );

        return false;
      } finally {
        submitLock.current =
          false;

        dispatch(
          setReturnBusy(false)
        );
      }
    };

  // =====================================================
  // 🧹 RESET
  // =====================================================

  const handlerResetReturnEvent =
    () => {
      dispatch(
        resetReturnEvent()
      );

      idempotencyKey.current =
        undefined;

      submitLock.current =
        false;
    };

  // =====================================================
  // 🔄 Venta inicial
  // =====================================================

  useEffect(() => {
    if (
      isOpen &&
      initialSaleId
    ) {
      void handlerAddDocument(
        undefined,
        initialSaleId
      );
    }
  }, [
    isOpen,
    initialSaleId,
  ]);

  // =====================================================
  // 🧹 Limpiar al cerrar
  // =====================================================

  useEffect(() => {
    if (!isOpen) {
      handlerResetReturnEvent();
    }
  }, [isOpen]);

  // =====================================================
  // 🛡️ Evitar cierre navegador
  // =====================================================

  useEffect(() => {
    const preventUnload = (
      event: BeforeUnloadEvent
    ) => {
      if (!busy) {
        return;
      }

      event.preventDefault();

      event.returnValue = '';
    };

    window.addEventListener(
      'beforeunload',
      preventUnload
    );

    return () =>
      window.removeEventListener(
        'beforeunload',
        preventUnload
      );
  }, [busy]);

  // =====================================================
  // 📤 API DEL HOOK
  // =====================================================

  return {
    // Estado
    documentResults,
    documentPaginator,
    searchingDocuments,

    documents,
    selectedLines,
    quantities,
    unitPrices,
    discounts,

    preview,
    previewLoading,
    result,

    busy,
    error,

    // Derivados
    request,
    validationErrors,

    // Helpers
    returnLineKey,

    // Handlers
    handlerSearchDocuments,

    handlerAddDocument,
    handlerRemoveDocument,

    handlerChangeSelection,
    handlerChangeQuantity,
    handlerChangeUnitPrice,
    handlerChangeDiscount,
    handlerSetAll,

    handlerPreview,
    handlerSubmit,

    handlerResetReturnEvent,
    invalidatePreview,
  };
};

export default useReturnEvent;